import { SLSService } from '@core/services/secure-localstorage.service';
import { AppConstants } from 'app/shared/constants/app.constants';
import { EnumConstants } from 'app/shared/enum/enum.constants';

export class Helper {
  // Filter Array
  // https://gist.github.com/jherax/f11d669ba286f21b7a2dcff69621eb72
  public static filterArray(array, filters) {
    const filterKeys = Object.keys(filters);
    return array.filter((item) => {
      // validates all filter criteria
      return filterKeys.every((key) => {
        // ignores non-function predicates
        if (typeof filters[key] !== 'function') return true;
        return filters[key](item[key]);
      });
    });
  }

  public static getFileName(moduleName, details) {
    const now = new Date();
    const pad = (n: number) => String(n).padStart(2, '0');
    const formattedDate = `${pad(now.getDate())}${pad(now.getMonth() + 1)}${String(now.getFullYear()).slice(-2)}_${pad(now.getHours())}${pad(now.getMinutes())}${pad(now.getSeconds())}`;
    const formattedDetails = details.replace(/\s/g, '_');
    return `${moduleName}_${formattedDetails}_${formattedDate}`;
  }

  public static getInvoiceNumber(churchCode, currentCount) {
    return `${churchCode}-${currentCount + 1}`;
  }

  public static getCurrentWeek(): Date[] {
    const now = new Date();
    const day = now.getDay();
    const diffToMonday = (day === 0 ? -6 : 1 - day);
    const weekStart = new Date(now);
    weekStart.setDate(now.getDate() + diffToMonday);
    weekStart.setHours(0, 0, 0, 0);
    const days: Date[] = [];
    for (let i = 0; i <= 6; i++) {
      const d = new Date(weekStart);
      d.setDate(weekStart.getDate() + i);
      days.push(d);
    }
    return days;
  }

  public static getCurrentWeekBirthAnniversaryMembers(memberData) {
    const currentYear = new Date().getFullYear();
    const currentWeekDates = Helper.getCurrentWeek();
    const birthDateMembers = [];
    const anniversaryDateMembers = [];
    for (const memberObj of memberData) {
      for (const weekDate of currentWeekDates) {
        const normalizeDate = (d: Date) => { const n = new Date(d); n.setFullYear(currentYear); n.setHours(0, 0, 0, 0); return n.getTime(); };
        const weekTs = normalizeDate(weekDate);
        if (memberObj.birthDate && normalizeDate(new Date(memberObj.birthDate)) === weekTs) {
          birthDateMembers.push(memberObj);
        }
        if (memberObj.anniversaryDate && normalizeDate(new Date(memberObj.anniversaryDate)) === weekTs) {
          anniversaryDateMembers.push(memberObj);
        }
      }
    }
    return {
      birthdayMembers: birthDateMembers,
      anniversaryMembers: anniversaryDateMembers,
    };
  }

  public static findPercentage(percentToGet, totalValue) {
    return (percentToGet / 100) * totalValue;
  }

  public static amountInWords(num) {
    const a = [
      '',
      'one ',
      'two ',
      'three ',
      'four ',
      'five ',
      'six ',
      'seven ',
      'eight ',
      'nine ',
      'ten ',
      'eleven ',
      'twelve ',
      'thirteen ',
      'fourteen ',
      'fifteen ',
      'sixteen ',
      'seventeen ',
      'eighteen ',
      'nineteen ',
    ];
    const b = ['', '', 'twenty', 'thirty', 'forty', 'fifty', 'sixty', 'seventy', 'eighty', 'ninety'];
    if ((num = num.toString()).length > 9) return 'overflow';
    let n: any;
    n = ('000000000' + num).substr(-9).match(/^(\d{2})(\d{2})(\d{2})(\d{1})(\d{2})$/);
    if (!n) return;
    var str = '';
    str += n[1] != 0 ? (a[Number(n[1])] || b[n[1][0]] + ' ' + a[n[1][1]]) + 'crore ' : '';
    str += n[2] != 0 ? (a[Number(n[2])] || b[n[2][0]] + ' ' + a[n[2][1]]) + 'lakh ' : '';
    str += n[3] != 0 ? (a[Number(n[3])] || b[n[3][0]] + ' ' + a[n[3][1]]) + 'thousand ' : '';
    str += n[4] != 0 ? (a[Number(n[4])] || b[n[4][0]] + ' ' + a[n[4][1]]) + 'hundred ' : '';
    str += n[5] != 0 ? (str != '' ? 'and ' : '') + (a[Number(n[5])] || b[n[5][0]] + ' ' + a[n[5][1]]) : '';
    return str;
  }

  public static toTitleCase(str) {
    const tempStr = str.toString();
    return tempStr.replace(/\w\S*/g, function (txt) {
      return txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase();
    });
  }

  public static titleCaseAddress(str) {
    if (str) {
      const splitStr = str.toLowerCase().split(',');
      for (let i = 0; i < splitStr.length; i++) {
        splitStr[i] = splitStr[i].trim();
        splitStr[i] = splitStr[i].charAt(0).toUpperCase() + splitStr[i].substring(1);
      }
      return splitStr.join(', ');
    } else {
      return str;
    }
  }

  public static diffStringArray(arr1, arr2) {
    return [...diff(arr1, arr2), ...diff(arr2, arr1)];

    function diff(a, b) {
      return a.filter((item) => b.indexOf(item) === -1);
    }
  }

  public static sortAlphaNum(list, key, order) {
    let sortAlphaNum;
    if (order === 'asc') {
      sortAlphaNum = (a, b) => a[key]?.localeCompare(b[key], 'en', { numeric: true });
    } else {
      sortAlphaNum = (a, b) => b[key]?.localeCompare(a[key], 'en', { numeric: true });
    }
    return list.sort(sortAlphaNum);
  }

  public static generateNameBasedOnNameFormat(title, firstName, lastName, churchMemberId?) {
    const churchDetails = JSON.parse(SLSService.getValueByKey('associatedChurch'));
    const nameFormat = churchDetails.nameFormat;

    const tempTitle = title ? Helper.toTitleCase(title) + '.' : '';
    const tempFirstName = firstName && firstName !== '.' && firstName !== '-' ? ' ' + Helper.toTitleCase(firstName) : '';
    const tempLastName = lastName && lastName !== '.' && lastName !== '-' ? ' ' + Helper.toTitleCase(lastName) : '';
    const tempChurchMemberId = churchMemberId ? ' (' + churchMemberId.toUpperCase() + ')' : '';

    let formattedName = '';
    if (nameFormat === EnumConstants.nameFormat.firstNameLastName) {
      formattedName = `${tempTitle}${tempFirstName}${tempLastName}${tempChurchMemberId}`;
    } else if (nameFormat === EnumConstants.nameFormat.firstNameDotLastName) {
      formattedName = `${tempTitle}${tempFirstName}${tempLastName !== '' ? (tempFirstName !== '' ? '.' + tempLastName : tempLastName) : ''}${tempChurchMemberId}`;
    } else if (nameFormat === EnumConstants.nameFormat.firstNameCapsDotLastName) {
      formattedName = `${tempTitle}${tempFirstName.toUpperCase()}${tempLastName !== '' ? (tempFirstName !== '' ? '.' + tempLastName : tempLastName) : ''}${tempChurchMemberId}`;
    } else if (nameFormat === EnumConstants.nameFormat.lastNameFirstName) {
      formattedName = `${tempTitle}${tempLastName}${tempFirstName}${tempChurchMemberId}`;
    } else if (nameFormat === EnumConstants.nameFormat.lastNameDotFirstName) {
      formattedName = `${tempTitle}${tempLastName}${tempFirstName !== '' ? (tempLastName !== '' ? '.' + tempFirstName : tempFirstName) : ''}${tempChurchMemberId}`;
    } else if (nameFormat === EnumConstants.nameFormat.lastNameCapsDotFirstName) {
      formattedName = `${tempTitle}${tempLastName.toUpperCase()}${tempFirstName !== '' ? (tempLastName !== '' ? '.' + tempFirstName : tempFirstName) : ''}${tempChurchMemberId}`;
    }

    formattedName = formattedName.replace(/\s+/g, ' ').trim().replace(/\.$/, '');
    return formattedName;
  }

  public static getAge(dateOfBirth): string {
    const today = new Date();
    const birthdate = new Date(dateOfBirth);
    let years = today.getFullYear() - birthdate.getFullYear();
    const m = today.getMonth() - birthdate.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birthdate.getDate())) years--;
    return years <= 0 ? 'Less than 1 yr' : years + ' yr';
  }

  public static convertToPlain(html) {
    // Create a new div element
    var tempDivElement = document.createElement('div');
    // Set the HTML content with the given value
    tempDivElement.innerHTML = html;
    // Retrieve the text property of the element
    return tempDivElement.textContent || tempDivElement.innerText || '';
  }

  public static collatePermissions(roles: any[]): string[] {
    const combinedPermissions = (roles ?? []).flatMap((r: any) => (r?.permissions ?? []) as string[]);

    return [...new Set<string>(combinedPermissions)];
  }

  /**
   * Permissions for portal routing / menu / AuthGuard.
   * Staff login: **only** `staff.permissions` (what was assigned on the staff record) — never partner `roles`,
   * otherwise staff would inherit the whole store role and see every sidebar item.
   * Partner login: collated `roles[].permissions`.
   */
  public static effectiveUserPermissions(user: { loginType?: string; staff?: { permissions?: string[] }; roles?: any[] } | null): string[] {
    if (!user) {
      return [];
    }
    if (user.loginType === 'staff') {
      const staffPerms = user.staff && Array.isArray(user.staff.permissions) ? user.staff.permissions : [];
      return [...new Set(staffPerms)];
    }
    return Helper.collatePermissions(user.roles || []) || [];
  }
}
